import React, { useState, useEffect } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Smartphone, ShieldCheck, FileText, Users2,
  Settings, Bell, LogOut, MessageCircle, Building2, BadgeCheck, Gift,
  Database, Sparkles, FileText as FileTextIcon, Menu, Coins,
  Activity, Trophy, PackageOpen, Wallet, Link2, Sparkle, Gift as GiftIcon, Leaf, HeartPulse, Sliders,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import ThemeToggle from "@/components/ThemeToggle";
import ProfileMenu from "@/components/ProfileMenu";
import { NAV, AUTH } from "@/constants/testIds";

const NAV_ITEMS = [
  { to: "/app", label: "Dashboard", icon: LayoutDashboard, tid: NAV.dashboard, roles: ["customer", "b2b_admin", "b2b_agent", "support", "logistics", "admin"] },
  { to: "/app/devices", label: "Devices", icon: Smartphone, tid: NAV.devices, roles: ["customer", "b2b_admin", "b2b_agent", "admin"] },
  { to: "/app/subscription", label: "Subscription", icon: BadgeCheck, tid: NAV.subscription, roles: ["customer", "b2b_admin", "admin"] },
  { to: "/app/claims", label: "Claims", icon: FileText, tid: NAV.claims, roles: ["customer", "b2b_admin", "b2b_agent", "support", "logistics", "admin"] },
  { to: "/app/referrals", label: "Refer & earn", icon: Gift, tid: "nav-referrals", roles: ["customer", "b2b_admin", "admin"] },
  { to: "/app/gems", label: "Gems", icon: Coins, tid: "nav-gems", roles: ["customer", "b2b_admin", "b2b_agent", "support", "logistics", "admin", "sales", "marketing"] },
  { to: "/app/wallet", label: "Wallet", icon: Wallet, tid: "nav-wallet", roles: ["customer", "b2b_admin", "b2b_agent", "support", "logistics", "admin", "sales", "marketing"] },
  { to: "/app/impact", label: "My Impact", icon: Leaf, tid: "nav-impact", roles: ["customer", "b2b_admin", "b2b_agent", "admin"] },
  { to: "/app/challenges", label: "Challenges", icon: Trophy, tid: "nav-challenges", roles: ["customer", "b2b_admin", "b2b_agent", "admin"] },
  { to: "/app/gifts", label: "Gift Cards", icon: GiftIcon, tid: "nav-gifts", roles: ["customer", "b2b_admin", "b2b_agent", "admin"] },
  { to: "/app/wrapped", label: "My Wrapped", icon: Sparkle, tid: "nav-wrapped", roles: ["customer", "b2b_admin", "admin"] },
  { to: "/app/health-score", label: "Device Health", icon: HeartPulse, tid: "nav-health", roles: ["customer", "b2b_admin", "b2b_agent", "admin"] },
  { to: "/app/agent/payment-links", label: "Payment links", icon: Link2, tid: "nav-payment-links", roles: ["admin", "b2b_agent", "b2b_admin", "support"] },
  { to: "/app/b2b", label: "B2B Portal", icon: Building2, tid: NAV.b2b, roles: ["b2b_admin", "b2b_agent", "admin"] },
  { to: "/app/crm", label: "CRM", icon: Sparkles, tid: "nav-crm", roles: ["admin", "support", "sales", "marketing", "b2b_admin", "customer", "logistics"] },
  { to: "/app/notifications", label: "Notifications", icon: Bell, tid: NAV.notifications, roles: ["customer", "b2b_admin", "b2b_agent", "support", "logistics", "admin"] },
  { to: "/app/admin", label: "Admin", icon: Settings, tid: NAV.admin, roles: ["admin"] },
  { to: "/app/admin/analytics", label: "Ops Analytics", icon: Activity, tid: "nav-analytics", roles: ["admin"] },
  { to: "/app/admin/raffle", label: "Raffle Admin", icon: Trophy, tid: "nav-raffle", roles: ["admin"] },
  { to: "/app/admin/loaners", label: "Loaners", icon: PackageOpen, tid: "nav-loaners", roles: ["admin", "logistics", "support"] },
  { to: "/app/admin/wallets", label: "Wallet Admin", icon: Wallet, tid: "nav-wallet-admin", roles: ["admin"] },
  { to: "/app/admin/enhancements", label: "Platform Settings", icon: Sliders, tid: "nav-enhancements", roles: ["admin"] },
  { to: "/app/admin/masters", label: "Master Data", icon: Database, tid: "nav-masters", roles: ["admin"] },
  { to: "/app/admin/content", label: "Content", icon: FileTextIcon, tid: "nav-content", roles: ["admin", "marketing"] },
];

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [counts, setCounts] = useState({ total_actionable: 0 });
  const [hasAgent, setHasAgent] = useState(false);

  const items = NAV_ITEMS.filter(i => i.roles.includes(user?.role));

  // Check once whether the user has a linked Referrer profile — if so we
  // surface the Partner Portal link. Silently ignored on 404.
  useEffect(() => {
    if (!user) return;
    let mounted = true;
    api.get("/agent/me")
      .then(() => { if (mounted) setHasAgent(true); })
      .catch(() => { if (mounted) setHasAgent(false); });
    return () => { mounted = false; };
  }, [user]);

  // Poll task counts every 60s so the nav badge stays fresh without WebSocket infra.
  // Only for staff-ish roles (customer/b2b_agent skip — no admin task dashboard).
  useEffect(() => {
    if (!user) return undefined;
    const staffRoles = ["admin","support","sales","marketing","logistics","b2b_admin"];
    if (!staffRoles.includes(user.role)) return undefined;
    let mounted = true;
    const tick = () => api.get("/crm/tasks/counts")
      .then(r => { if (mounted) setCounts(r.data); })
      .catch(() => {});
    tick();
    const iv = setInterval(tick, 60_000);
    return () => { mounted = false; clearInterval(iv); };
  }, [user]);

  const badgeFor = (item) => (item.to === "/app/crm" ? counts.total_actionable : 0);

  // Dynamically append Partner Portal link if this user is a linked referrer.
  const displayItems = hasAgent
    ? [...items, { to: "/app/agent", label: "Partner Portal", icon: Coins, tid: "nav-agent" }]
    : items;

  return (
    <div className="min-h-screen bg-background grain">
      <aside className="fixed inset-y-0 left-0 z-30 hidden lg:flex w-64 flex-col border-r border-border bg-card">
        <div className="px-6 py-6 border-b border-border">
          <Link to="/app" className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-primary flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-secondary" />
            </div>
            <div>
              <div className="font-display font-semibold text-lg tracking-tight">Treasurescare</div>
            </div>
          </Link>
        </div>
        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1 scrollbar-thin">
          {displayItems.map(item => {
            const Icon = item.icon;
            const badge = badgeFor(item);
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/app"}
                data-testid={item.tid}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-primary text-primary-foreground shadow-teal-glow"
                      : "text-foreground/75 hover:bg-muted hover:text-foreground dark:text-foreground/90"
                  }`
                }
              >
                <Icon className="w-4 h-4" />
                <span className="flex-1">{item.label}</span>
                {badge > 0 && (
                  <span
                    className="ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-rose-500 text-white text-[10px] font-semibold"
                    data-testid={`nav-badge-${item.tid}`}
                  >
                    {badge > 99 ? "99+" : badge}
                  </span>
                )}
              </NavLink>
            );
          })}
        </nav>
        <div className="p-4 border-t border-border">
          <div className="flex items-center gap-3">
            <Avatar className="w-9 h-9">
              <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                {(user?.name || "?").split(" ").map(s => s[0]).slice(0,2).join("").toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold truncate">{user?.name}</div>
              <div className="text-[11px] text-muted-foreground truncate capitalize">{user?.role?.replace("_"," ")}</div>
            </div>
            <ThemeToggle compact />
          </div>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="lg:hidden sticky top-0 z-30 glass px-3 py-3 flex items-center justify-between">
        <div className="flex items-center gap-1">
          <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
            <SheetTrigger asChild>
              <Button size="icon" variant="ghost" data-testid="mobile-nav-toggle" aria-label="Open menu">
                <Menu className="w-5 h-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-72 p-0 flex flex-col">
              <SheetTitle className="sr-only">Navigation</SheetTitle>
              <div className="px-5 py-4 border-b border-border flex items-center gap-2">
                <div className="w-9 h-9 rounded-xl bg-primary flex items-center justify-center">
                  <ShieldCheck className="w-5 h-5 text-secondary" />
                </div>
                <div className="font-display font-semibold text-lg tracking-tight">Treasurescare</div>
              </div>
              <nav className="flex-1 overflow-y-auto px-3 py-3 space-y-1" data-testid="mobile-nav-drawer">
                {displayItems.map(item => {
                  const Icon = item.icon;
                  const badge = badgeFor(item);
                  return (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      end={item.to === "/app"}
                      onClick={() => setDrawerOpen(false)}
                      data-testid={`mobile-${item.tid}`}
                      className={({ isActive }) =>
                        `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                          isActive
                            ? "bg-primary text-primary-foreground"
                            : "text-foreground/80 hover:bg-muted"
                        }`
                      }
                    >
                      <Icon className="w-4 h-4" />
                      <span className="flex-1">{item.label}</span>
                      {badge > 0 && (
                        <span className="ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-rose-500 text-white text-[10px] font-semibold">
                          {badge > 99 ? "99+" : badge}
                        </span>
                      )}
                    </NavLink>
                  );
                })}
              </nav>
              <div className="p-4 border-t border-border">
                <div className="flex items-center gap-3">
                  <Avatar className="w-9 h-9">
                    <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                      {(user?.name || "?").split(" ").map(s => s[0]).slice(0,2).join("").toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold truncate">{user?.name}</div>
                    <div className="text-[11px] text-muted-foreground truncate capitalize">{user?.role?.replace("_"," ")}</div>
                  </div>
                  <ThemeToggle compact />
                </div>
              </div>
            </SheetContent>
          </Sheet>
          <Link to="/app" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
              <ShieldCheck className="w-4 h-4 text-secondary" />
            </div>
            <span className="font-display font-semibold text-base">Treasurescare</span>
          </Link>
        </div>
        <ProfileMenu />
      </div>
      {/* Mobile bottom nav — 4 most-used shortcuts + "More" opens the drawer */}
      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-30 glass border-t border-border py-2 flex justify-around">
        {items.slice(0,4).map(item => {
          const Icon = item.icon;
          const badge = badgeFor(item);
          return (
            <NavLink key={item.to} to={item.to} end={item.to === "/app"} className={({isActive}) => `relative flex flex-col items-center gap-0.5 text-[10px] px-2 py-1 ${isActive ? "text-primary" : "text-muted-foreground"}`}>
              <Icon className="w-5 h-5" />
              {badge > 0 && (
                <span className="absolute -top-0.5 right-1 min-w-[16px] h-4 px-1 rounded-full bg-rose-500 text-white text-[9px] font-semibold flex items-center justify-center">
                  {badge > 9 ? "9+" : badge}
                </span>
              )}
              <span>{item.label.split(" ")[0]}</span>
            </NavLink>
          );
        })}
        <button
          type="button"
          onClick={() => setDrawerOpen(true)}
          data-testid="mobile-nav-more"
          className="relative flex flex-col items-center gap-0.5 text-[10px] px-2 py-1 text-muted-foreground"
        >
          <Menu className="w-5 h-5" />
          {counts.total_actionable > 0 && (
            <span className="absolute -top-0.5 right-1 min-w-[16px] h-4 px-1 rounded-full bg-rose-500 text-white text-[9px] font-semibold flex items-center justify-center">
              {counts.total_actionable > 9 ? "9+" : counts.total_actionable}
            </span>
          )}
          <span>More</span>
        </button>
      </nav>

      <main className="lg:pl-64 pb-20 lg:pb-0">
        {/* Desktop top bar with ProfileMenu */}
        <div className="hidden lg:flex sticky top-0 z-20 h-14 items-center justify-end gap-3 px-6 border-b border-border bg-background/80 backdrop-blur">
          <ProfileMenu />
        </div>
        <div className="max-w-7xl mx-auto px-4 lg:px-8 py-6 lg:py-10 min-h-[calc(100vh-4rem)]">
          {children}
        </div>
        <footer className="lg:pl-0 border-t border-border mt-8 py-6">
          <div className="max-w-7xl mx-auto px-4 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-muted-foreground">
            <div className="flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-primary" />
              <span>Treasurescare · © SRAM &amp; MRAM LIMITED, UK</span>
            </div>
            <div className="flex items-center gap-4">
              <Link to="/about" className="hover:text-foreground transition-colors">About</Link>
              <Link to="/contact" className="hover:text-foreground transition-colors">Contact</Link>
              <a href="mailto:support@treasurescare.com" className="hover:text-foreground transition-colors">Support</a>
              <Link to="/delete-account" className="hover:text-rose-500 transition-colors" data-testid="footer-delete-account">Delete account</Link>
            </div>
          </div>
        </footer>
      </main>
    </div>
  );
}
